// Typed extension surface over @strudel/web / superdough.
//
// Strudel's audio registry lives in the `superdough` package and is
// re-exported up through `@strudel/webaudio` → `@strudel/web`. We pull the
// targets from the captured module ref first (see player.ts:getStrudelModule)
// and fall back to `globalThis` because initStrudel() also installs some of
// these as globals (e.g. `samples`).
//
// Each wrapper is a thin pass-through with a typed signature and one
// behaviour of its own: throw `EngineNotReadyError` with an actionable
// message if the target can't be resolved. This keeps every consumer from
// redoing the same probing + error handling.

import { getStrudelModule } from './player';
import type {
  SampleMap,
  SamplesOptions,
  SoundEntry,
  SoundHandler,
  SoundMeta,
  StrudelModule,
} from './types';

export class EngineNotReadyError extends Error {
  constructor(missing: string) {
    super(
      `Strudel engine extension "${missing}" is not available. ` +
        `Call initPlayer() and await its promise before using extensions.`,
    );
    this.name = 'EngineNotReadyError';
  }
}

// Subset of globalThis properties we probe. Typed so each lookup site is
// assignment-compatible with the module's exported signature.
type GlobalExtras = {
  registerSound?: StrudelModule['registerSound'];
  samples?: StrudelModule['samples'];
  registerWorklet?: StrudelModule['registerWorklet'];
  soundAlias?: StrudelModule['soundAlias'];
  getSound?: StrudelModule['getSound'];
};

function resolve<K extends keyof GlobalExtras>(
  name: K,
): NonNullable<GlobalExtras[K]> {
  const mod = getStrudelModule();
  const fromMod = mod?.[name];
  if (typeof fromMod === 'function') {
    return fromMod as NonNullable<GlobalExtras[K]>;
  }
  const fromGlobal = (globalThis as GlobalExtras)[name];
  if (typeof fromGlobal === 'function') {
    return fromGlobal as NonNullable<GlobalExtras[K]>;
  }
  throw new EngineNotReadyError(name);
}

/** Register a custom sound-handler under `key`. Overwrites any existing
 *  handler at the same key. superdough lowercases + underscore-normalises
 *  the key before storing it, so `"My Kick"` becomes `"my_kick"`.
 *
 *  The returned handler is invoked as
 *    `await onTrigger(startTime, hapValue, onEnded, cps)`
 *  and must return `{ node, stop, nodes?, triggerRelease? }` or `undefined`
 *  to indicate a silent voice. See types.ts SoundHandler for the contract. */
export function registerSound(
  key: string,
  onTrigger: SoundHandler,
  data?: SoundMeta,
): void {
  resolve('registerSound')(key, onTrigger, data);
}

/** Load samples from a URL, github-shorthand, strudel.json document, or
 *  inline object map. Accepted map shapes are documented on SampleMap.
 *  Resolves once the manifest is indexed; individual audio blobs stream
 *  lazily on first trigger. */
export async function samples(
  map: SampleMap,
  baseUrl?: string,
  options?: SamplesOptions,
): Promise<void> {
  // `async` keeps resolve()'s EngineNotReadyError a rejection rather than a
  // sync throw — consumers always `await` this, so symmetry matters.
  // Older superdough versions typed this void; the Promise.resolve wrap
  // below normalises the return.
  await Promise.resolve(resolve('samples')(map, baseUrl, options));
}

/** Register an AudioWorklet module URL so custom DSP nodes become
 *  available inside sound-handlers via `new AudioWorkletNode(ctx, name)`.
 *  Registration is once-per-AudioContext; calling again with the same URL
 *  is a no-op in modern browsers. */
export async function registerWorklet(url: string): Promise<void> {
  await Promise.resolve(resolve('registerWorklet')(url));
}

/** Alias one registered sound name to another. Both names resolve to the
 *  same handler; the alias can be used in `s("…")` patterns immediately. */
export function soundAlias(original: string, alias: string): void {
  resolve('soundAlias')(original, alias);
}

/** Look up a registered sound's entry (handler + meta). Returns undefined
 *  if the key isn't registered. Useful for tooling — e.g. listing all
 *  user-registered samples, or checking whether a name is already taken. */
export function getSound(key: string): SoundEntry | undefined {
  return resolve('getSound')(key);
}

/** True once initPlayer() has booted @strudel/web and the extension
 *  surface is callable. Prefer this over try/catching the wrappers when
 *  rendering conditional UI. */
export function isEngineReady(): boolean {
  const mod = getStrudelModule();
  if (!mod) return false;
  return typeof mod.registerSound === 'function' ||
    typeof (globalThis as GlobalExtras).registerSound === 'function';
}
